import { apiObjUrl } from "src/common/apiUrl";
import { onError } from "src/common/onError";
import { rest } from "src/common/rest";
import { restWithObjectVersion } from "src/common/restWithObjectVersion";
import { ZAttribute, ZAttributeValue, zAttribute } from "src/types/ZAttribute";
import { ZGroup, zGroup } from "src/types/ZGroup";
import { ZIdName } from "src/types/ZIdName";
import { ZRestriction, zRestriction } from "src/types/ZRestriction";
import { ZValueThings, zValueThings } from "src/types/ZValueThings";
import { z } from "zod";
import { zUid, ZUid } from "src/types/zUid";
import { ZObjectItem, zObjectItem } from "../../types/ZObjectItem";
import { AttrSaveConfig } from "./Obj2Tab/EdAttribute";
import {
  ZLightGroup,
  ZLightObject,
  zLightGroup,
  zLightObject,
} from "./Obj2Tab/Obj2Nodes";

export const loadObjects = async (): Promise<ZObjectItem[]> => {
  const response = await restWithObjectVersion.get(apiObjUrl("/objects"));

  // const objects = response.data as ZObjectItem[];
  // const cvtGroup = (g: ZGroup) => {
  //   if (g.groupType.id === 1) {
  //     g.groupType.id = 2;
  //     g.groupType.name = GroupType.ByDictionary;
  //   }
  //   cvtGroups(g.groups);
  // };
  // const cvtGroups = (list: ZGroup[] | null | undefined) =>
  //   list?.forEach(cvtGroup);
  // objects.forEach((obj) => {
  //   cvtGroups(obj.groups);
  // });

  return zObjectItem.array().parse(response.data);
};

export const createObject = async (
  newObject: Omit<ZObjectItem, "id">,
): Promise<ZObjectItem> => {
  const resp = await rest.post(apiObjUrl(`/objects`), newObject);
  return zObjectItem.parse(resp.data);
};

export const updateObject = async (
  srcObject: Omit<ZObjectItem, "attributes" | "groups">,
  svData: Partial<ZObjectItem>,
): Promise<ZObjectItem> => {
  const { id } = srcObject;
  await rest.put(apiObjUrl(`/objects/${id}`), { ...svData, id });
  const resp = await restWithObjectVersion.get(apiObjUrl(`/objects/${id}`));
  const newObj = zObjectItem.parse(resp.data);
  return newObj;
};

export const createLightObject = async (
  newObject: Omit<ZLightObject, "id">,
): Promise<ZObjectItem> => {
  const resp = await rest.post(apiObjUrl(`/objects`), newObject);
  return zObjectItem.parse(resp.data);
};

export const saveLightObject = async (
  objectId: ZUid,
  values: Partial<ZLightObject>,
): Promise<ZLightObject> => {
  await rest.put(apiObjUrl(`/objects/${objectId}`), {
    ...values,
    id: objectId,
  });
  const resp = await restWithObjectVersion.get(
    apiObjUrl(`/objects/${objectId}`),
  );
  const newObj = zLightObject.parse(resp.data);
  return newObj;
};

export const deleteObject = async (objId: ZUid) => {
  await rest.delete(apiObjUrl(`/objects/${objId}`));
};

export const loadObjectAttrinbutesAll = async (
  objectId: ZUid,
  options: { translate?: boolean } = {},
) => {
  const attsResp = await restWithObjectVersion.get(
    apiObjUrl(`/objects/${objectId}/all-attributes`),
    { translate: options.translate },
  );
  return zAttribute.array().parse(attsResp.data);
};

export const zSupportedFilterAttrData = z.object({
  id: zUid,
  operations: z.string().array(),
});

export type ZSupportedFilterAttrData = z.infer<typeof zSupportedFilterAttrData>;

export const loadFilterSupportedAtts = async (objectId: ZUid) => {
  const attsResp = await restWithObjectVersion.get(
    apiObjUrl(`/objects/${objectId}/filter-support-attributes`),
  );
  return zSupportedFilterAttrData.array().parse(attsResp.data);
};

export const loadObjectAttrinbutesReduced = async (
  objectId: ZUid,
  options: { translate?: boolean } = {},
) => {
  const attsResp = await restWithObjectVersion.get(
    apiObjUrl(`/objects/${objectId}/attributes`),
    { translate: options.translate },
  );
  return zAttribute.array().parse(attsResp.data);
};

export const makeObjectAttributesMap = (list: ZAttribute[]) => {
  const objectAttsMap = list.reduce(
    (acc, curr) => {
      acc[curr.id] = curr;
      return acc;
    },
    {} as Record<ZUid, ZAttribute>,
  );
  return objectAttsMap;
};

export const loadAttributeInfo = async (
  attrId: ZUid,
  options: { translate?: boolean } = {},
): Promise<ZAttribute> => {
  const resp = await restWithObjectVersion.get(
    apiObjUrl(`/attributes/${attrId}`),
    { translate: options.translate },
  );
  return zAttribute.parse(resp.data);
};

export const createAttributeInObject = async (
  objId: ZUid,
  attr: Omit<ZAttribute, "id">,
  config?: AttrSaveConfig,
): Promise<ZAttribute> => {
  const resp = await rest.post(
    apiObjUrl(`/objects/${objId}/attributes`),
    attr,
    config,
  );
  return zAttribute.parse(resp.data);
};

export const deleteAttributeFromObject = async (objId: ZUid, attrId: ZUid) => {
  await rest.delete(apiObjUrl(`/objects/${objId}/attributes/${attrId}`));
};

export const deleteAttribute = async (attrId: ZUid) => {
  await rest.delete(apiObjUrl(`/attributes/${attrId}`));
};

export const deleteAttributeFromValue = async (
  groupId: ZUid,
  valueId: ZUid,
  attrId: ZUid,
) => {
  await rest.delete(
    apiObjUrl(`/groups/${groupId}/values/${valueId}/attributes/${attrId}`),
  );
};

export const saveAttribute = async (
  attr: ZAttribute,
  config?: AttrSaveConfig,
) => {
  await rest.put(apiObjUrl(`/attributes/${attr.id}`), attr, config);
};

export const updateAttribute = async (
  attr: ZAttribute,
  config?: AttrSaveConfig,
): Promise<ZAttribute> => {
  await saveAttribute(attr, config);
  await restWithObjectVersion.get(
    apiObjUrl(`/attributes/${attr.id}/restrictions`),
  );
  const resp = await restWithObjectVersion.get(
    apiObjUrl(`/attributes/${attr.id}`),
  );
  return zAttribute.parse(resp.data);
};

export const createGroupInObj = async (
  objId: ZUid,
  group: Omit<ZGroup, "id">,
): Promise<ZGroup> => {
  const resp = await rest.post(apiObjUrl(`/objects/${objId}/groups`), group);
  return zGroup.parse(resp.data);
};

export const createGroupInGroup = async (
  ownerGroupId: ZUid,
  group: Omit<ZGroup, "id">,
): Promise<ZGroup> => {
  const resp = await rest.post(
    apiObjUrl(`/groups/${ownerGroupId}/groups`),
    group,
  );
  return zGroup.parse(resp.data);
};

export const updateGroup = async (group: ZLightGroup): Promise<ZGroup> => {
  await rest.put(apiObjUrl(`/groups/${group.id}`), group);
  const resp = await rest.get(apiObjUrl(`/groups/${group.id}`));
  return { ...zLightGroup.parse(resp.data), attributeId: group.attributeId };
};

export const createAttrInGroup = async (
  groupId: ZUid,
  attr: Omit<ZAttribute, "id">,
  config?: AttrSaveConfig,
): Promise<ZAttribute> => {
  const resp = await rest.post(
    apiObjUrl(`/groups/${groupId}/attributes`),
    attr,
    config,
  );
  return zAttribute.parse(resp.data);
};

export const deleteGroup = async (groupId: ZUid) => {
  await rest.delete(apiObjUrl(`/groups/${groupId}`));
};

export const loadRestrictions = async (
  attrId: ZUid,
): Promise<ZRestriction | null> => {
  const resp = await restWithObjectVersion.get(
    apiObjUrl(`/attributes/${attrId}/restrictions`),
  );
  if (!resp.data) return null;
  return zRestriction.parse(resp.data);
};

export const saveRestrictions = async (
  attrId: ZUid,
  restrictions: ZRestriction,
) => {
  await rest.put(apiObjUrl(`/attributes/${attrId}/restrictions`), restrictions);
};

export const deleteRestrictions = async (attrId: ZUid) =>
  rest.delete(apiObjUrl(`/attributes/${attrId}/restrictions`));

export const loadDictValues = async (
  dictAttrId: ZUid,
): Promise<ZAttributeValue[] | undefined> => {
  try {
    const resp = await restWithObjectVersion.get(
      apiObjUrl(`/attributes/${dictAttrId}`),
    );
    const attr = zAttribute.parse(resp.data);
    return attr.values ?? [];
  } catch (e) {
    onError(e);
    return undefined;
  }
};

export const loadDict = async (dictAttrId: ZUid): Promise<ZIdName[]> => {
  try {
    const resp = await restWithObjectVersion.get(
      apiObjUrl(`/attributes/${dictAttrId}`),
    );
    const attr = zAttribute.parse(resp.data);
    return (attr.values ?? []).map(({ id, value, displayId }) => ({
      id,
      name: String(value),
      displayId,
    }));
  } catch (e) {
    onError(e);
    return [];
  }
};

export const loadFromAttr = async (attrId: ZUid): Promise<ZIdName[]> => {
  try {
    const resp = await restWithObjectVersion.get(
      apiObjUrl(`/attributes/${attrId}`),
    );
    const attr = zAttribute.parse(resp.data);
    const { referenceId } = attr;
    if (!referenceId)
      throw Error(`Атрибут ${attr.name} не содержит справочника`);
    return await loadDict(referenceId);
  } catch (e) {
    onError(e);
  }
  return [];
};

export const loadValueThings = async (
  groupId: ZUid,
  valueId: ZUid,
): Promise<ZValueThings> => {
  const resp = await rest.get(
    apiObjUrl(`/groups/${groupId}/values/${valueId}`),
  );
  return zValueThings.parse(resp.data);
};

export const apiAddExistingAttr = async (
  groupId: ZUid,
  valueId: ZUid,
  attrId: ZUid,
): Promise<void> => {
  await rest.post(
    apiObjUrl(`/groups/${groupId}/values/${valueId}/attributes/${attrId}`),
  );
};

export const createAttrInValue = async (
  groupId: ZUid,
  valueId: ZUid,
  body: Omit<ZAttribute, "id">,
  config?: AttrSaveConfig,
): Promise<ZAttribute> => {
  const resp = await rest.post(
    apiObjUrl(`/groups/${groupId}/values/${valueId}/attributes`),
    body,
    config,
  );
  return zAttribute.parse(resp.data);
};

export const createGroupInValue = async (
  groupId: ZUid,
  valueId: ZUid,
  body: Omit<ZGroup, "id">,
) => {
  const resp = await rest.post(
    apiObjUrl(`/groups/${groupId}/values/${valueId}/groups`),
    body,
  );
  return zLightGroup.parse(resp.data);
};
